import { Box, MenuItem, TextField } from "@mui/material";
import { FC, useState } from "react";
import {
  ATTENDANCE_STATUS_LABELS,
  AttendanceFilterOptions,
  AttendanceStatus,
} from "src/apis/dashboardAttendance";
import SearchAndResetButtons from "src/pages/components/SearchAndResetButtons";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import {
  getDayFromWeekBefore,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getToday,
} from "src/utils/date";

export type AttendanceFiltersState = {
  companyId: string;
  status: string;
  unit: string;
  sector: string;
  serviceType: string;
  startDate: string;
  endDate: string;
};

type AttendanceFiltersProps = {
  isAdmin: boolean;
  options: AttendanceFilterOptions | null;
  state: AttendanceFiltersState;
  onChange: (patch: Partial<AttendanceFiltersState>) => void;
  isLoading: boolean;
  onSearch: () => void;
  onReset: () => void;
};

const STATUS_OPTIONS = Object.keys(ATTENDANCE_STATUS_LABELS) as AttendanceStatus[];

const getLast30Days = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 29);
  return date.toISOString().split("T")[0];
};

const selectSx = { minWidth: 170 };

const AttendanceFilters: FC<AttendanceFiltersProps> = ({
  isAdmin,
  options,
  state,
  onChange,
  isLoading,
  onSearch,
  onReset,
}): JSX.Element => {
  const [period, setPeriod] = useState<string>("month");

  const applyPreset = (preset: string) => {
    setPeriod(preset);
    switch (preset) {
      case "today":
        onChange({ startDate: getToday(), endDate: getToday() });
        break;
      case "7d":
        onChange({ startDate: getDayFromWeekBefore(), endDate: getToday() });
        break;
      case "30d":
        onChange({ startDate: getLast30Days(), endDate: getToday() });
        break;
      case "month":
        onChange({ startDate: getFirstDayOfMonth(), endDate: getLastDayOfMonth() });
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.75rem",
        alignItems: "center",
        width: "100%",
        mb: 1,
      }}
    >
      <TextField
        select
        label="Período"
        value={period}
        onChange={(e) => applyPreset(e.target.value)}
        sx={selectSx}
      >
        <MenuItem value="today">Hoje</MenuItem>
        <MenuItem value="7d">Últimos 7 dias</MenuItem>
        <MenuItem value="30d">Últimos 30 dias</MenuItem>
        <MenuItem value="month">Mês atual</MenuItem>
        <MenuItem value="custom">Personalizado</MenuItem>
      </TextField>

      {period === "custom" && (
        <StartAndEndDateFilters
          startDate={state.startDate}
          setStartDate={(dt) => onChange({ startDate: dt })}
          endDate={state.endDate}
          setEndDate={(dt) => onChange({ endDate: dt })}
        />
      )}

      {isAdmin && (
        <TextField
          select
          label="Empresa"
          value={state.companyId}
          onChange={(e) => onChange({ companyId: e.target.value })}
          sx={selectSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {(options?.companies ?? []).map((company) => (
            <MenuItem key={company.id} value={String(company.id)}>
              {company.name ?? `#${company.id}`}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label="Unidade"
        value={state.unit}
        onChange={(e) => onChange({ unit: e.target.value })}
        sx={selectSx}
      >
        <MenuItem value="">Todas</MenuItem>
        {(options?.units ?? []).map((unit) => (
          <MenuItem key={unit} value={unit}>
            {unit}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Setor"
        value={state.sector}
        onChange={(e) => onChange({ sector: e.target.value })}
        sx={selectSx}
      >
        <MenuItem value="">Todos</MenuItem>
        {(options?.sectors ?? []).map((sector) => (
          <MenuItem key={sector} value={sector}>
            {sector}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Status"
        value={state.status}
        onChange={(e) => onChange({ status: e.target.value })}
        sx={selectSx}
      >
        <MenuItem value="">Todos</MenuItem>
        {STATUS_OPTIONS.map((status) => (
          <MenuItem key={status} value={status}>
            {ATTENDANCE_STATUS_LABELS[status]}
          </MenuItem>
        ))}
      </TextField>

      {(options?.service_types ?? []).length > 0 && (
        <TextField
          select
          label="Tipo de atendimento"
          value={state.serviceType}
          onChange={(e) => onChange({ serviceType: e.target.value })}
          sx={selectSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {(options?.service_types ?? []).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
      )}

      <SearchAndResetButtons
        isLoading={isLoading}
        search={onSearch}
        resetState={onReset}
      />
    </Box>
  );
};

export default AttendanceFilters;
